import React, { useState } from 'react';
import {
  FaSave,
  FaPlus,
  FaEdit,
  FaTrash,
  FaHandshake,
  FaTimes,
  FaCheckCircle,
  FaExclamationCircle,
  FaBuilding,
  FaTag,
} from 'react-icons/fa';
import { useAdmin } from '../context/AdminContext';
import './SectionManager.css';
import './PartnersManager.css';

const PartnersManager = () => {
  const { data, addItem, updateItem, deleteItem, loading } = useAdmin();
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '' });
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const partnersList = data?.partners || [];

  const notifySuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');

    if (!form.name.trim()) {
      setErrorMsg('Partner corporate entity name is required.');
      return;
    }

    try {
      if (editId) {
        await updateItem('partners', editId, { name: form.name.trim() });
        notifySuccess('Corporate partner updated successfully!');
      } else {
        await addItem('partners', { name: form.name.trim() });
        notifySuccess('New corporate partner registered successfully!');
      }
      resetForm();
    } catch (err) {
      console.error('Error saving partner:', err);
      setErrorMsg('Failed to save partner entity. Please try again.');
    }
  };

  const handleEdit = (item) => {
    setForm({ name: item.name || '' });
    setEditId(item.id);
    setShowForm(true);
    setErrorMsg('');
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to remove this corporate partner?')) {
      await deleteItem('partners', id);
      notifySuccess('Corporate partner removed successfully.');
    }
  };

  const resetForm = () => {
    setForm({ name: '' });
    setEditId(null);
    setShowForm(false);
    setErrorMsg('');
  };

  const getMonogram = (name = '') => {
    if (!name) return 'P';
    const words = name.trim().split(' ');
    if (words.length >= 2) {
      return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  return (
    <div className="sg-manager">
      {/* ═══════════════════════════════════════════
          PAGE HEADER
         ═══════════════════════════════════════════ */}
      <div className="sg-page-header">
        <div className="sg-ph-left">
          <div className="sg-ph-icon">
            <FaHandshake />
          </div>
          <div className="sg-ph-text">
            <h2>Partners &amp; Affiliates</h2>
            <p>Manage strategic business alliances, corporate clients, and partner network</p>
          </div>
        </div>
        <div className="sg-ph-right">
          <button className="sg-btn-gold" onClick={() => setShowForm(!showForm)}>
            {showForm ? (
              <>
                <FaTimes /> Close Form
              </>
            ) : (
              <>
                <FaPlus /> Register Partner
              </>
            )}
          </button>
        </div>
      </div>

      {/* Action Alerts */}
      {successMsg && (
        <div className="sg-alert sg-alert-success">
          <FaCheckCircle /> {successMsg}
        </div>
      )}

      {errorMsg && (
        <div className="sg-alert sg-alert-error">
          <FaExclamationCircle /> {errorMsg}
        </div>
      )}

      {/* ═══════════════════════════════════════════
          FORM CONTAINER
         ═══════════════════════════════════════════ */}
      {showForm && (
        <div className="sg-form-card">
          <div className="sg-form-header">
            <div>
              <h3>{editId ? 'Modify Partner Affiliation' : 'Register New Partner'}</h3>
              <p>Add official brand or conglomerate name to be showcased on public tickers</p>
            </div>
            <button className="sg-form-close" onClick={resetForm}>
              <FaTimes />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="sg-form">
            <div className="sg-form-row">
              <div className="sg-field">
                <label>
                  <FaBuilding style={{ color: '#c9a227', marginRight: '6px' }} />
                  Partner Entity Name <span className="req">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Tata Group / UltraTech Cement / Apollo Hospitals"
                  maxLength={100}
                  required
                />
              </div>

              {/* Live Preview Card */}
              <div className="sg-field">
                <label>
                  <FaTag style={{ color: '#c9a227', marginRight: '6px' }} />
                  Ticker Display Preview
                </label>
                <div className="pm-live-preview">
                  <div className="pm-preview-monogram">
                    {getMonogram(form.name)}
                  </div>
                  <div className="pm-preview-text">
                    <strong>{form.name.trim() || 'Partner Entity Name'}</strong>
                    <span>Verified Corporate Network</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="sg-form-actions">
              <button type="submit" className="sg-btn-gold">
                <FaSave /> {editId ? 'Update Partner' : 'Confirm Partnership'}
              </button>
              <button
                type="button"
                className="sg-btn-outline"
                onClick={resetForm}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ═══════════════════════════════════════════
          DATA VIEW (TABLE LIST)
         ═══════════════════════════════════════════ */}
      <div className="sg-table-card">
        <div className="sg-table-header">
          <div>
            <h3>All Strategic Alliances</h3>
            <p>Active corporate logos and text displayed across homepage and portfolio carousels</p>
          </div>
          <span className="sg-count-badge">{partnersList.length} Total Partners</span>
        </div>

        <div className="sg-table-wrap">
          <table className="sg-table">
            <thead>
              <tr>
                <th style={{ width: '80px' }}>Index</th>
                <th style={{ width: '100px' }}>Monogram</th>
                <th>Corporate Partner / Brand Name</th>
                <th style={{ width: '180px' }}>Network Status</th>
                <th style={{ width: '120px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {partnersList.length === 0 ? (
                <tr>
                  <td colSpan="5" className="sg-empty">
                    <div className="sg-empty-inner">
                      <FaHandshake />
                      <h4>No Strategic Partners Registered</h4>
                      <p>Click "Register Partner" above to add your first corporate alliance</p>
                    </div>
                  </td>
                </tr>
              ) : (
                partnersList.map((item, i) => (
                  <tr key={item.id || i}>
                    <td>
                      <span className="pm-index-badge">{String(i + 1).padStart(2, '0')}</span>
                    </td>
                    <td>
                      <div className="pm-monogram-badge">
                        {getMonogram(item.name)}
                      </div>
                    </td>
                    <td>
                      <strong className="sg-row-title">{item.name || 'Untitled Partner'}</strong>
                    </td>
                    <td>
                      <span className="pm-status-pill">
                        <FaCheckCircle /> Active Alliance
                      </span>
                    </td>
                    <td>
                      <div className="sg-actions">
                        <button
                          className="sg-action-btn sg-edit"
                          onClick={() => handleEdit(item)}
                          title="Edit Partner"
                        >
                          <FaEdit />
                        </button>
                        <button
                          className="sg-action-btn sg-delete"
                          onClick={() => handleDelete(item.id)}
                          title="Remove Partner"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PartnersManager;